import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity, Users, MousePointerClick, Eye, LogOut, RefreshCw, Sun, Moon,
  Mail, Ban, RotateCcw, Trash2, Plus, Link2, Copy, BarChart2, X, Clock, ExternalLink, FileDown,
  Upload, FileText, UserPlus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const tooltipStyle = {
  contentStyle: { background: "#0D1220", border: "1px solid #1E293B", borderRadius: 0, fontFamily: "JetBrains Mono, monospace", fontSize: 12, color: "#F1F5F9" },
  labelStyle: { color: "#94A3B8" },
};

const STATUS_STYLE = {
  Invited: "text-[#FCD34D] border-[rgba(245,158,11,0.4)] bg-[rgba(245,158,11,0.08)]",
  Opened: "text-[#10B981] border-[rgba(16,185,129,0.4)] bg-[rgba(16,185,129,0.08)]",
  Expired: "text-[#94A3B8] border-line2 bg-[rgba(148,163,184,0.08)]",
  Revoked: "text-[#FCA5A5] border-[rgba(248,113,113,0.4)] bg-[rgba(248,113,113,0.08)]",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [investors, setInvestors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", firm: "", send_email: false, sandbox_access: false, business_model_access: false, introducer_name: "", introducer_email: "", tier: "full" });
  const [creating, setCreating] = useState(false);
  const [analyticsFor, setAnalyticsFor] = useState(null); // { investor, sections, ctas, total_dwell_ms }
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionsBusy, setSectionsBusy] = useState({});
  const [bmUrl, setBmUrl] = useState("");
  const [bmUrlDraft, setBmUrlDraft] = useState("");
  const [bmSaving, setBmSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(null); // null | "main" | "appendix"
  const [materials, setMaterials] = useState({ one_pager: null, five_pager: null, twelve_pager_slide_ids: [] });
  const [materialsBusy, setMaterialsBusy] = useState({});
  const [twelveDraft, setTwelveDraft] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [connForm, setConnForm] = useState({ name: "", email: "" });
  const [connBusy, setConnBusy] = useState(false);

  const downloadDeckPdf = async (withAppendix) => {
    const key = withAppendix ? "appendix" : "main";
    setPdfBusy(key);
    try {
      const res = await api.get(`/admin/export/pdf?appendix=${withAppendix ? "true" : "false"}`, { responseType: "blob", timeout: 120000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `streamline360-deck-${withAppendix ? "with-appendix" : "main"}-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Deck PDF downloaded.");
    } catch (e) {
      const msg = formatApiErrorDetail(e) || "PDF generation failed. Please try again.";
      toast.error(msg);
    } finally {
      setPdfBusy(null);
    }
  };

  const fmtMs = (ms) => {
    const s = Math.round((ms || 0) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };

  const openAnalytics = async (iv) => {
    setAnalyticsFor({ investor: iv, sections: [], ctas: [], total_dwell_ms: 0 });
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get(`/admin/investors/${iv.id}/analytics`);
      setAnalyticsFor(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load analytics");
      setAnalyticsFor(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [inv, a, s, st, m, c] = await Promise.all([
        api.get("/admin/investors"),
        api.get("/admin/analytics"),
        api.get("/admin/sections"),
        api.get("/admin/settings"),
        api.get("/admin/materials"),
        api.get("/admin/connectors"),
      ]);
      setInvestors(inv.data);
      setAnalytics(a.data);
      setSections(s.data);
      setBmUrl(st.data.business_model_url || "");
      setBmUrlDraft(st.data.business_model_url || "");
      setMaterials(m.data);
      setTwelveDraft(m.data.twelve_pager_slide_ids || []);
      setConnectors(c.data);
    } catch {} finally { setLoading(false); }
  };

  const saveBmUrl = async () => {
    setBmSaving(true);
    try {
      const { data } = await api.post("/admin/settings/business_model_url", { value: bmUrlDraft });
      setBmUrl(data.value || "");
      setBmUrlDraft(data.value || "");
      toast.success(data.value ? "Business model URL saved" : "Business model URL cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save URL");
    } finally {
      setBmSaving(false);
    }
  };

  const togglePromoted = async (slide_id, next) => {
    setSectionsBusy((b) => ({ ...b, [slide_id]: true }));
    try {
      await api.post(`/admin/sections/${slide_id}/promote`, { promoted: next });
      setSections((list) => list.map((s) => (s.id === slide_id ? { ...s, promoted: next } : s)));
      toast.success(next ? "Moved to main deck — investors will see it" : "Moved back to appendix — hidden from investors");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update visibility");
    } finally {
      setSectionsBusy((b) => ({ ...b, [slide_id]: false }));
    }
  };

  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const createInvestor = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        introducer_name: form.introducer_name?.trim() || null,
        introducer_email: form.introducer_email?.trim() || null,
      };
      const { data } = await api.post("/admin/investors", payload);
      const pdfTier = data.tier === "one_pager" || data.tier === "five_pager";
      if (form.send_email && data.email_sent) {
        toast.success(pdfTier
          ? `Investor added — PDF emailed to ${data.email}`
          : `Investor added — personal link emailed to ${data.email}`);
      } else if (form.send_email) {
        toast.error(data.email_error || "Investor added but the email could not be sent");
      } else if (data.magic_url) {
        try { await navigator.clipboard.writeText(data.magic_url); } catch {}
        toast.success(`Investor added — personal link copied to clipboard`);
      } else {
        toast.success(`Investor added · tick "Email the PDF" to send the ${data.tier.replace('_','-')}`);
      }
      setForm({ name: "", email: "", firm: "", send_email: false, sandbox_access: false, business_model_access: false, introducer_name: "", introducer_email: "", tier: form.tier });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create investor");
    } finally { setCreating(false); }
  };

  const uploadMaterial = async (tier, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF must be under 15 MB"); return; }
    setMaterialsBusy((b) => ({ ...b, [tier]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/materials/${tier}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMaterials((m) => ({ ...m, [tier]: data }));
      toast.success(`${tier === "one_pager" ? "1-pager" : "5-pager"} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setMaterialsBusy((b) => ({ ...b, [tier]: false }));
    }
  };

  const previewMaterial = async (tier) => {
    try {
      const res = await api.get(`/admin/materials/${tier}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not preview PDF");
    }
  };

  const toggleTwelveSlide = (id) => {
    setTwelveDraft((d) => d.includes(id) ? d.filter((x) => x !== id) : (d.length < 12 ? [...d, id] : d));
  };

  const saveTwelvePager = async () => {
    setMaterialsBusy((b) => ({ ...b, twelve: true }));
    try {
      const { data } = await api.post("/admin/materials/twelve-pager", { slide_ids: twelveDraft });
      setMaterials((m) => ({ ...m, twelve_pager_slide_ids: data.slide_ids }));
      setTwelveDraft(data.slide_ids);
      toast.success(`12-pager saved — ${data.slide_ids.length} slide${data.slide_ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save 12-pager");
    } finally {
      setMaterialsBusy((b) => ({ ...b, twelve: false }));
    }
  };

  const inviteConnector = async (e) => {
    e.preventDefault();
    if (!connForm.name || !connForm.email) { toast.error("Name and email are required"); return; }
    setConnBusy(true);
    try {
      const { data } = await api.post("/admin/connectors", connForm);
      if (data.email_sent) toast.success(`Setup link emailed to ${data.email}`);
      else {
        try { await navigator.clipboard.writeText(data.setup_link); } catch {}
        toast.success(`Connector invited — setup link copied to clipboard`);
      }
      setConnForm({ name: "", email: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not invite");
    } finally { setConnBusy(false); }
  };

  const revokeConnector = async (id, email) => {
    if (!window.confirm(`Revoke connector ${email}? Their login stops working immediately.`)) return;
    try {
      await api.delete(`/admin/connectors/${id}`);
      toast.success("Connector revoked");
      await load();
    } catch { toast.error("Could not revoke"); }
  };

  const copyLink = async (url, email) => {
    if (!url) { toast.error("No link available"); return; }
    try { await navigator.clipboard.writeText(url); toast.success(`Personal link for ${email} copied`); }
    catch { toast.error("Could not copy link"); }
  };

  const rotateLink = async (id, email) => {
    if (!window.confirm(`Rotate the personal link for ${email}? The old link will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/investors/${id}/rotate-link`);
      try { await navigator.clipboard.writeText(data.magic_url); } catch {}
      toast.success(`New link generated and copied for ${email}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not rotate link");
    }
  };

  const sendIntro = async (id, email) => {
    try {
      const { data } = await api.post(`/admin/investors/${id}/invite-email`);
      if (data.email_sent) toast.success(`Personal link emailed to ${email}`);
      else toast.error(data.email_error || "Email failed to send");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email");
    }
  };

  const setSandbox = async (id, enabled) => {
    try { await api.post(`/admin/investors/${id}/sandbox`, { enabled }); toast.success(enabled ? "Sandbox link included" : "Sandbox link hidden"); await load(); }
    catch { toast.error("Could not update sandbox access"); }
  };

  const setBusinessModel = async (id, enabled) => {
    try { await api.post(`/admin/investors/${id}/business-model`, { enabled }); toast.success(enabled ? "Business model shared with investor" : "Business model hidden from investor"); await load(); }
    catch { toast.error("Could not update business-model access"); }
  };

  const revoke = async (id) => {
    try { await api.post(`/admin/investors/${id}/revoke`); toast.success("Access revoked"); await load(); }
    catch { toast.error("Could not revoke"); }
  };
  const restore = async (id) => {
    try { await api.post(`/admin/investors/${id}/restore`); toast.success("Access restored"); await load(); }
    catch { toast.error("Could not restore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this investor permanently?")) return;
    try { await api.delete(`/admin/investors/${id}`); toast.success("Investor deleted"); await load(); }
    catch { toast.error("Could not delete"); }
  };

  const kpis = [
    { key: "investors", icon: Users, label: "Investors", value: investors.length },
    { key: "opened", icon: Eye, label: "Signed in", value: investors.filter((i) => i.status === "Opened").length },
    { key: "events", icon: Activity, label: "Engagement events", value: analytics?.total_events ?? "—" },
    { key: "ctas", icon: MousePointerClick, label: "CTA clicks", value: (analytics?.cta_clicks || []).reduce((s, x) => s + x.count, 0) },
  ];

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-ink/90 backdrop-blur-md px-6 py-3 lg:px-10">
        <div className="flex items-center gap-2">
          <Logo imgClass="h-8" />
          <span className="ml-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">Founder Console</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block font-mono-data text-xs text-ftext3">{user?.email}</span>
          <button data-testid="admin-theme-toggle" onClick={toggle} aria-label="Toggle theme" className="flex items-center justify-center w-8 h-8 border border-line2 text-ftext2 hover:text-sky hover:border-sky transition-colors">
            {theme === "light" ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
          </button>
          <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Link data-testid="admin-preview-deck" to="/" target="_blank" rel="noopener" className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-sky hover:text-skyH transition-colors border border-sky/40 px-2 py-1">
            Preview Deck ↗
          </Link>
          <button
            data-testid="admin-export-pdf-main"
            onClick={() => downloadDeckPdf(false)}
            disabled={pdfBusy !== null}
            title="Download the main deck as a 16:9 PDF"
            className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky hover:border-sky transition-colors border border-line2 px-2 py-1 disabled:opacity-60 disabled:cursor-wait"
          >
            <FileDown className="w-3.5 h-3.5" />
            {pdfBusy === "main" ? "Rendering…" : "PDF (main)"}
          </button>
          <button
            data-testid="admin-export-pdf-appendix"
            onClick={() => downloadDeckPdf(true)}
            disabled={pdfBusy !== null}
            title="Download the full deck including Appendix slides"
            className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky hover:border-sky transition-colors border border-line2 px-2 py-1 disabled:opacity-60 disabled:cursor-wait"
          >
            <FileDown className="w-3.5 h-3.5" />
            {pdfBusy === "appendix" ? "Rendering…" : "PDF (+appendix)"}
          </button>
          <button data-testid="admin-logout-btn" onClick={doLogout} className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-[#FCA5A5] transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 py-8 lg:px-10 space-y-8">
        {/* KPIs */}
        <div className="grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((k) => (
            <div key={k.key} data-testid={`kpi-${k.key}`} className="bg-panel p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono-data text-xs uppercase tracking-wider text-ftext3">{k.label}</span>
                <k.icon className="w-4 h-4 text-sky" />
              </div>
              <div className="font-mono-data font-bold text-3xl text-ftext">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Create investor */}
        <div className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Create an investor · personal link, no password · access valid 21 days
          </div>
          <form onSubmit={createInvestor} className="p-5 grid gap-3 md:grid-cols-3 md:items-end">
            <Field label="Material / tier">
              <select data-testid="inv-tier-select" value={form.tier} onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))} className="w-full rounded-none bg-ink border border-line2 px-3 py-2 text-sm text-ftext focus:outline-none focus:border-sky h-[42px]">
                <option value="full">Full deck · web link</option>
                <option value="twelve_pager">12-pager · web link</option>
                <option value="five_pager">5-pager · PDF attachment</option>
                <option value="one_pager">1-pager · PDF attachment</option>
              </select>
            </Field>
            <Field label="Full name">
              <Input data-testid="inv-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Investor" className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]" />
            </Field>
            <Field label="Email">
              <Input data-testid="inv-email-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@fund.com" className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]" />
            </Field>
            <Field label="Firm (optional)">
              <Input data-testid="inv-firm-input" value={form.firm} onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))} placeholder="Northstar Capital" className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]" />
            </Field>
            <Field label="Introduced by (optional)">
              <Input data-testid="inv-introducer-name-input" value={form.introducer_name} onChange={(e) => setForm((f) => ({ ...f, introducer_name: e.target.value }))} placeholder="John Doe" className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]" />
            </Field>
            <Field label="Introducer email (CC on invite)">
              <Input data-testid="inv-introducer-email-input" type="email" value={form.introducer_email} onChange={(e) => setForm((f) => ({ ...f, introducer_email: e.target.value }))} placeholder="john@introducer.com" className="rounded-none bg-ink border-line2 focus-visible:ring-[#38BDF8]" />
            </Field>
            <label className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-data text-xs text-ftext2 select-none">
              <span className="flex items-center gap-2 cursor-pointer">
                <input data-testid="inv-sendemail-checkbox" type="checkbox" checked={form.send_email} onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))} className="accent-[#38BDF8]" />
                Email them the personal link now
              </span>
              <span className="flex items-center gap-2 cursor-pointer">
                <input data-testid="inv-sandbox-checkbox" type="checkbox" checked={form.sandbox_access} onChange={(e) => setForm((f) => ({ ...f, sandbox_access: e.target.checked }))} className="accent-[#38BDF8]" />
                Include sandbox link for this investor
              </span>
              <span className="flex items-center gap-2 cursor-pointer">
                <input data-testid="inv-bm-checkbox" type="checkbox" checked={form.business_model_access} onChange={(e) => setForm((f) => ({ ...f, business_model_access: e.target.checked }))} className="accent-[#10B981]" />
                Share business model with this investor
              </span>
            </label>
            <button data-testid="inv-create-btn" type="submit" disabled={creating} className="flex items-center justify-center gap-2 bg-sky hover:bg-skyH text-primary-foreground font-mono-data text-sm font-semibold uppercase tracking-wider px-4 py-2.5 transition-colors disabled:opacity-60 h-[42px]">
              <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create + get link"}
            </button>
          </form>
        </div>

        {/* Charts */}
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Section views">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.section_views || []} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke="#1E293B" horizontal={false} />
                <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis type="category" dataKey="section" stroke="#64748B" fontSize={11} tickLine={false} width={80} />
                <Tooltip cursor={{ fill: "rgba(56,189,248,0.08)" }} {...tooltipStyle} />
                <Bar dataKey="count" fill="#38BDF8" maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="CTA clicks">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics?.cta_clicks || []} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke="#1E293B" horizontal={false} />
                <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis type="category" dataKey="cta" stroke="#64748B" fontSize={11} tickLine={false} width={90} />
                <Tooltip cursor={{ fill: "rgba(245,158,11,0.08)" }} {...tooltipStyle} />
                <Bar dataKey="count" fill="#F59E0B" maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Business model URL — the shared link that investors with "BIZ-MODEL: On" will see */}
        <div data-testid="business-model-setting" className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 flex items-center justify-between gap-3">
            <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3">
              Business Model Simulator · shared link
            </div>
            <div className={`font-mono-data text-[10px] uppercase tracking-wider ${bmUrl ? "text-[#10B981]" : "text-ftext3"}`}>
              {bmUrl ? "Link set — slide live for enabled investors" : "No link — slide stays hidden for everyone"}
            </div>
          </div>
          <div className="px-5 py-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <input
              type="url"
              data-testid="business-model-url-input"
              value={bmUrlDraft}
              onChange={(e) => setBmUrlDraft(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/… · Notion · Docsend · any share link"
              className="flex-1 bg-ink border border-line2 px-3 py-2 text-sm text-ftext focus:outline-none focus:border-sky"
            />
            <button
              type="button"
              data-testid="business-model-url-save"
              disabled={bmSaving || bmUrlDraft === bmUrl}
              onClick={saveBmUrl}
              className="font-mono-data text-xs font-black uppercase tracking-wider border border-sky bg-sky/10 text-sky px-4 py-2 hover:bg-sky/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {bmSaving ? "Saving…" : bmUrl ? "Update link" : "Save link"}
            </button>
            {bmUrl && (
              <a
                href={bmUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="business-model-url-open"
                className="font-mono-data text-xs uppercase tracking-wider border border-line2 text-ftext2 hover:text-sky hover:border-sky px-3 py-2 inline-flex items-center gap-1.5 transition-colors whitespace-nowrap"
              >
                Open <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <p className="px-5 pb-4 text-[11px] text-ftext3 leading-snug">
            Once saved, every investor row below gets a "BIZ-MODEL" toggle. Flip it on and that investor sees the slide titled "Streamline360 — Business Model Simulator" with a button opening this link in a new tab. Change the link anytime — all enabled investors instantly see the new one.
          </p>
        </div>

        {/* Materials — PDFs + 12-pager slide picker (founder-only) */}
        <div data-testid="materials-panel" className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Materials · 1-pager & 5-pager PDFs · 12-pager slide picker
          </div>
          <div className="p-5 grid gap-4 md:grid-cols-2">
            {["one_pager", "five_pager"].map((tier) => {
              const m = materials[tier];
              const label = tier === "one_pager" ? "1-pager PDF" : "5-pager PDF";
              return (
                <div key={tier} data-testid={`material-${tier}`} className="border border-line2 p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider text-ftext2"><FileText className="w-3.5 h-3.5" /> {label}</div>
                    <span className={`font-mono-data text-[10px] uppercase tracking-wider ${m ? "text-[#10B981]" : "text-ftext3"}`}>{m ? "Uploaded" : "Not uploaded"}</span>
                  </div>
                  {m && (
                    <div className="font-mono-data text-[11px] text-ftext3 leading-snug">
                      <div className="truncate">{m.filename}</div>
                      <div>{Math.round((m.size || 0) / 1024)} KB · updated {fmt(m.updated_at)}</div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <label className={`flex items-center justify-center gap-2 cursor-pointer font-mono-data text-[11px] font-black uppercase tracking-wider border px-3 py-1.5 ${materialsBusy[tier] ? "opacity-50 cursor-wait" : "border-sky text-sky hover:bg-sky/10"}`}>
                      <Upload className="w-3 h-3" /> {materialsBusy[tier] ? "Uploading…" : (m ? "Replace" : "Upload")}
                      <input data-testid={`material-upload-${tier}`} type="file" accept="application/pdf" className="hidden" disabled={materialsBusy[tier]} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadMaterial(tier, f); }} />
                    </label>
                    {m && (
                      <button data-testid={`material-preview-${tier}`} type="button" onClick={() => previewMaterial(tier)} className="font-mono-data text-[11px] uppercase tracking-wider border border-line2 text-ftext2 hover:text-sky hover:border-sky px-3 py-1.5">Preview</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-line p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-data text-xs uppercase tracking-wider text-ftext2">12-pager · pick up to 12 slides (order you tick is the order shown)</div>
              <div className="font-mono-data text-[11px] text-ftext3">{twelveDraft.length}/12 selected</div>
            </div>
            <div className="grid gap-1.5 md:grid-cols-3 lg:grid-cols-4 max-h-64 overflow-y-auto border border-line2 p-2">
              {sections.map((s) => {
                const idx = twelveDraft.indexOf(s.id);
                const picked = idx !== -1;
                return (
                  <label key={s.id} data-testid={`twelve-pick-${s.id}`} className={`flex items-center gap-2 px-2 py-1.5 border cursor-pointer transition-colors ${picked ? "border-sky/60 bg-sky/10" : "border-transparent hover:bg-line/40"}`}>
                    <input type="checkbox" checked={picked} onChange={() => toggleTwelveSlide(s.id)} className="accent-[#38BDF8]" />
                    <span className="font-mono-data text-[11px] text-ftext2 truncate flex-1">{s.label || s.id}</span>
                    {picked && <span className="font-mono-data text-[10px] text-sky tabular-nums">{idx + 1}</span>}
                  </label>
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-end">
              <button data-testid="twelve-pager-save" type="button" disabled={materialsBusy.twelve} onClick={saveTwelvePager} className="font-mono-data text-xs font-black uppercase tracking-wider border border-sky bg-sky/10 text-sky px-4 py-2 hover:bg-sky/20 disabled:opacity-50">
                {materialsBusy.twelve ? "Saving…" : "Save 12-pager"}
              </button>
            </div>
          </div>
        </div>

        {/* Connectors (founder-only) */}
        <div data-testid="connectors-panel" className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Connectors · external people who can share the 1-pager / 5-pager
          </div>
          <form onSubmit={inviteConnector} className="p-5 grid gap-3 md:grid-cols-3 md:items-end">
            <Field label="Full name">
              <Input data-testid="connector-name-input" value={connForm.name} onChange={(e) => setConnForm((f) => ({ ...f, name: e.target.value }))} placeholder="Alex Partner" className="rounded-none bg-ink border-line2" />
            </Field>
            <Field label="Email">
              <Input data-testid="connector-email-input" type="email" value={connForm.email} onChange={(e) => setConnForm((f) => ({ ...f, email: e.target.value }))} placeholder="alex@partner.com" className="rounded-none bg-ink border-line2" />
            </Field>
            <button data-testid="connector-invite-btn" type="submit" disabled={connBusy} className="flex items-center justify-center gap-2 bg-sky hover:bg-skyH text-primary-foreground font-mono-data text-sm font-semibold uppercase tracking-wider px-4 py-2.5 disabled:opacity-60 h-[42px]">
              <UserPlus className="w-4 h-4" /> {connBusy ? "Inviting…" : "Invite connector"}
            </button>
          </form>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line font-mono-data text-xs uppercase tracking-wider text-ftext3">
                  {["Connector", "Status", "Invited", "Activated", "Actions"].map((h) => <th key={h} className="text-left px-5 py-3 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody data-testid="connectors-table">
                {connectors.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-ftext3 font-mono-data text-xs">No connectors yet.</td></tr>}
                {connectors.map((c) => (
                  <tr key={c.id} data-testid={`connector-row-${c.email}`} className="border-b border-line/60 last:border-0">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-ftext">{c.name}</div>
                      <div className="font-mono-data text-xs text-ftext3">{c.email}</div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`font-mono-data text-[10px] uppercase tracking-wider border px-2 py-0.5 ${c.revoked ? "text-[#FCA5A5] border-[rgba(248,113,113,0.4)] bg-[rgba(248,113,113,0.08)]" : c.setup_at ? "text-[#10B981] border-[rgba(16,185,129,0.4)] bg-[rgba(16,185,129,0.08)]" : "text-[#FCD34D] border-[rgba(245,158,11,0.4)] bg-[rgba(245,158,11,0.08)]"}`}>
                        {c.revoked ? "Revoked" : c.setup_at ? "Active" : "Invited"}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext3 whitespace-nowrap">{fmt(c.created_at)}</td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext3 whitespace-nowrap">{fmt(c.setup_at)}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {!c.revoked && (
                        <button data-testid={`connector-revoke-${c.email}`} onClick={() => revokeConnector(c.id, c.email)} className="font-mono-data text-[11px] uppercase tracking-wider border border-line2 text-ftext2 hover:text-[#FCA5A5] hover:border-[#FCA5A5] px-2 py-1 inline-flex items-center gap-1.5">
                          <Ban className="w-3 h-3" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Deck sections — appendix visibility control */}
        {sections.filter((s) => s.appendix).length > 0 && (
          <div data-testid="deck-sections-panel" className="border border-line bg-panel">
            <div className="border-b border-line px-5 py-3 flex items-center justify-between gap-3">
              <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3">
                Appendix slides · visibility to investors
              </div>
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">
                Toggle a slide to move it into the main deck for all investors
              </div>
            </div>
            <ul className="divide-y divide-line">
              {sections.filter((s) => s.appendix).map((s) => (
                <li key={s.id} data-testid={`section-row-${s.id}`} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`font-mono-data text-[10px] font-black uppercase tracking-[0.18em] px-2 py-0.5 border ${s.promoted ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]" : "border-line2 text-ftext3"}`}>
                      {s.promoted ? "In deck" : "Appendix"}
                    </span>
                    <span className="font-heading font-bold text-sm text-ftext truncate">{s.label}</span>
                    <span className="font-mono-data text-[11px] text-ftext3">#{s.id}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`section-toggle-${s.id}`}
                    disabled={sectionsBusy[s.id]}
                    onClick={() => togglePromoted(s.id, !s.promoted)}
                    className={`font-mono-data text-[11px] font-black uppercase tracking-wider border px-3 py-1.5 transition-colors ${s.promoted
                      ? "border-line2 text-ftext2 hover:border-sky hover:text-sky"
                      : "border-[#10B981]/60 text-[#10B981] hover:bg-[#10B981]/10"} disabled:opacity-50`}
                  >
                    {sectionsBusy[s.id] ? "Saving…" : (s.promoted ? "Move back to appendix" : "Move to main deck")}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Investors table */}
        <div className="border border-line bg-panel">
          <div className="border-b border-line px-5 py-3 font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Investors ({investors.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line font-mono-data text-xs uppercase tracking-wider text-ftext3">
                  {["Investor", "Status", "Expires", "Last active", "Engagement", "Sections", "Access", "Actions"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="investors-table">
                {investors.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-8 text-center text-ftext3 font-mono-data text-xs">No investors yet.</td></tr>
                )}
                {investors.map((iv) => (
                  <tr key={iv.id} data-testid={`inv-row-${iv.email}`} className="border-b border-line/60 last:border-0 align-top">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-ftext">{iv.name}</div>
                      <div className="font-mono-data text-xs text-ftext3">{iv.email}</div>
                      {iv.firm ? <div className="text-xs text-ftext2">{iv.firm}</div> : null}
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono-data text-[10px] uppercase tracking-wider border border-line2 text-ftext3 px-1.5 py-0.5">
                          {iv.tier === "one_pager" ? "1-pager" : iv.tier === "five_pager" ? "5-pager" : iv.tier === "twelve_pager" ? "12-pager" : "Full deck"}
                        </span>
                        {iv.created_by_role === "connector" && (
                          <span className="font-mono-data text-[10px] uppercase tracking-wider border border-[rgba(56,189,248,0.4)] text-sky px-1.5 py-0.5" title={`Invited by connector ${iv.created_by}`}>via connector</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`font-mono-data text-xs uppercase tracking-wider border px-2 py-0.5 ${STATUS_STYLE[iv.status] || ""}`}>{iv.status}</span>
                    </td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext3 whitespace-nowrap">{fmtDate(iv.expires_at)}</td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext3 whitespace-nowrap">{fmt(iv.last_active_at)}</td>
                    <td className="px-5 py-3 font-mono-data text-xs text-ftext2 whitespace-nowrap">{iv.views} views · {iv.ctas} CTAs</td>
                    <td className="px-5 py-3 text-xs text-ftext2 max-w-[200px]">
                      {iv.viewed_sections?.length ? iv.viewed_sections.join(", ") : "—"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {iv.tier === "full" ? (
                        <div className="flex flex-col gap-1">
                          <button
                            data-testid={`inv-sandbox-${iv.email}`}
                            onClick={() => setSandbox(iv.id, !iv.sandbox_access)}
                            title={iv.sandbox_access ? "Sandbox link included — click to remove" : "Sandbox hidden — click to include"}
                            className={`font-mono-data text-[10px] uppercase tracking-wider border px-2 py-0.5 transition-colors ${iv.sandbox_access ? "text-[#10B981] border-[rgba(16,185,129,0.4)] bg-[rgba(16,185,129,0.08)]" : "text-ftext3 border-line2 hover:text-sky hover:border-sky"}`}
                          >
                            Sandbox: {iv.sandbox_access ? "On" : "Off"}
                          </button>
                          <button
                            data-testid={`inv-bm-${iv.email}`}
                            onClick={() => setBusinessModel(iv.id, !iv.business_model_access)}
                            title={iv.business_model_access ? "Business model shared — click to hide" : "Business model hidden — click to share"}
                            className={`font-mono-data text-[10px] uppercase tracking-wider border px-2 py-0.5 transition-colors ${iv.business_model_access ? "text-[#10B981] border-[rgba(16,185,129,0.4)] bg-[rgba(16,185,129,0.08)]" : "text-ftext3 border-line2 hover:text-sky hover:border-sky"}`}
                          >
                            Biz-model: {iv.business_model_access ? "On" : "Off"}
                          </button>
                        </div>
                      ) : (
                        <span className="font-mono-data text-[10px] text-ftext3">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <IconBtn testid={`inv-analytics-${iv.email}`} title="View section-by-section engagement" onClick={() => openAnalytics(iv)}>
                          <BarChart2 className="w-3.5 h-3.5" />
                        </IconBtn>
                        {iv.magic_url && (
                          <>
                            <IconBtn testid={`inv-copylink-${iv.email}`} title="Copy personal link" onClick={() => copyLink(iv.magic_url, iv.email)}>
                              <Copy className="w-3.5 h-3.5" />
                            </IconBtn>
                            <IconBtn testid={`inv-rotatelink-${iv.email}`} title="Rotate personal link (invalidate old)" onClick={() => rotateLink(iv.id, iv.email)}>
                              <Link2 className="w-3.5 h-3.5" />
                            </IconBtn>
                          </>
                        )}
                        <IconBtn testid={`inv-email-${iv.email}`} title={iv.tier === "full" || iv.tier === "twelve_pager" ? "Email personal link" : "Email the PDF again"} onClick={() => sendIntro(iv.id, iv.email)}>
                          <Mail className="w-3.5 h-3.5" />
                        </IconBtn>
                        {iv.revoked ? (
                          <IconBtn testid={`inv-restore-${iv.email}`} title="Restore access" onClick={() => restore(iv.id)}>
                            <RotateCcw className="w-3.5 h-3.5" />
                          </IconBtn>
                        ) : (
                          <IconBtn testid={`inv-revoke-${iv.email}`} title="Revoke access" onClick={() => revoke(iv.id)}>
                            <Ban className="w-3.5 h-3.5" />
                          </IconBtn>
                        )}
                        <IconBtn testid={`inv-delete-${iv.email}`} title="Delete" onClick={() => remove(iv.id)} danger>
                          <Trash2 className="w-3.5 h-3.5" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Analytics modal */}
      {analyticsFor && (
        <div
          data-testid="analytics-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setAnalyticsFor(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-line bg-ink"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <div>
                <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3">Engagement</div>
                <div className="font-heading font-bold text-lg text-ftext leading-tight">
                  {analyticsFor.investor?.name || "Investor"} <span className="text-ftext3 text-sm font-normal">· {analyticsFor.investor?.email}</span>
                </div>
              </div>
              <button
                data-testid="analytics-close-btn"
                onClick={() => setAnalyticsFor(null)}
                className="p-1.5 border border-line2 text-ftext2 hover:text-sky hover:border-sky transition-colors"
                aria-label="Close analytics"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              {analyticsLoading ? (
                <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3">Loading…</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-4 h-4 text-sky" />
                    <span className="font-mono-data text-xs uppercase tracking-wider text-ftext3">Total time on deck</span>
                    <span className="font-heading font-black text-xl text-ftext">{fmtMs(analyticsFor.total_dwell_ms)}</span>
                  </div>
                  <div className="mb-6">
                    <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-2">Time per section</div>
                    {analyticsFor.sections.length === 0 ? (
                      <div className="text-sm text-ftext3 border border-line px-4 py-3">No section engagement recorded yet.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {(() => {
                          const max = analyticsFor.sections.reduce((m, r) => Math.max(m, r.dwell_ms || 0), 0) || 1;
                          return analyticsFor.sections.map((r) => (
                            <div key={r.section} data-testid={`analytics-section-${r.section}`} className="flex items-center gap-3">
                              <div className="w-32 font-mono-data text-xs uppercase tracking-wider text-ftext2 shrink-0 truncate">{r.section}</div>
                              <div className="flex-1 h-3 bg-panel border border-line2 relative overflow-hidden">
                                <div className="h-full bg-sky" style={{ width: `${((r.dwell_ms || 0) / max) * 100}%` }} />
                              </div>
                              <div className="w-24 text-right font-mono-data text-xs text-ftext shrink-0 tabular-nums">{fmtMs(r.dwell_ms)}</div>
                              <div className="w-10 text-right font-mono-data text-[11px] text-ftext3 shrink-0 tabular-nums">×{r.visits}</div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                  {analyticsFor.ctas.length > 0 && (
                    <div>
                      <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-2">CTA interactions</div>
                      <div className="space-y-1.5">
                        {analyticsFor.ctas.map((c) => (
                          <div key={c.target} data-testid={`analytics-cta-${c.target}`} className="flex items-center gap-3 border border-line px-3 py-2">
                            <div className="flex-1 font-mono-data text-xs text-ftext">{c.target}</div>
                            <div className="font-mono-data text-xs font-bold text-sky tabular-nums">{c.count}×</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">{label}</span>
      {children}
    </div>
  );
}

function IconBtn({ children, title, onClick, testid, disabled, danger }) {
  return (
    <button
      data-testid={testid}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center w-8 h-8 border border-line2 text-ftext2 transition-colors disabled:opacity-30 ${danger ? "hover:text-[#FCA5A5] hover:border-[#FCA5A5]" : "hover:text-sky hover:border-sky"}`}
    >
      {children}
    </button>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="border border-line bg-panel p-5">
      <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-4">{title}</div>
      {children}
    </div>
  );
}
